"use client"

import { useEffect, useRef } from "react"
import type { KeyboardEvent, MouseEvent, RefObject } from "react"
import { useLatestRef } from "@/hooks/useLatestRef"
import { isFocusUnclaimed } from "./focusTracking.utils"

export interface FallbackTriggerState {
    hasFocus: boolean
    hasActivation: boolean
    cancelActivation?: () => void
}

export function useFallbackTrigger() {
    const fallbackRef = useRef<FallbackTriggerState>({
        hasFocus: false,
        hasActivation: false,
    })
    useEffect(() => () => fallbackRef.current.cancelActivation?.(), [])
    const fallbackHandlers = {
        onFocus: () => {
            fallbackRef.current.hasFocus = true
        },
        onClick: (event: MouseEvent<HTMLButtonElement>) => {
            const fallback = fallbackRef.current
            const standIn = event.currentTarget
            fallback.cancelActivation?.()
            const onPointerDown = (pointer: PointerEvent) => {
                if (
                    pointer.target instanceof Node &&
                    standIn.contains(pointer.target)
                )
                    return
                cancel()
            }
            const cancel = () => {
                fallback.hasActivation = false
                fallback.cancelActivation = undefined
                document.removeEventListener("pointerdown", onPointerDown, true)
            }
            document.addEventListener("pointerdown", onPointerDown, true)
            fallback.hasActivation = true
            fallback.cancelActivation = cancel
        },
        onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
            const cancel = fallbackRef.current.cancelActivation
            if (event.key !== "Escape" || !cancel) return
            event.preventDefault()
            cancel()
        },
    }
    return { fallbackRef, fallbackHandlers }
}

export function useFocusAfterFallback({
    fallbackRef,
    triggerRef,
    onActivated,
}: {
    fallbackRef?: RefObject<FallbackTriggerState>
    triggerRef: RefObject<HTMLElement | null>
    onActivated: () => void
}) {
    const onActivatedRef = useLatestRef(onActivated)
    useEffect(() => {
        const fallback = fallbackRef?.current
        if (!fallback) return
        const hasActivation = fallback.hasActivation
        fallback.cancelActivation?.()
        if (!isFocusUnclaimed(document.activeElement)) return
        if (fallback.hasFocus || hasActivation) triggerRef.current?.focus()
        if (hasActivation) onActivatedRef.current()
    }, [fallbackRef, triggerRef])
}
